import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Building2, Edit2, Trash2, X, Save,
  MapPin, Mail, Phone, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { getClients, createClient, updateClient, deleteClient, getApiError } from '../api';
import { useLanguage } from '../i18n';

const QdClients = () => {
  const { t, language: uiLang } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('qd_user') || '{}');
  const canDelete = ['admin', 'superadmin'].includes(user.role);
  
  useEffect(() => {
    loadClients();
  }, []);
  
  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (client) => {
    const confirmMsg = uiLang === 'lt' 
      ? `Ar tikrai norite ištrinti klientą "${client.name}"?`
      : `Are you sure you want to delete client "${client.name}"?`;
    if (!confirm(confirmMsg)) return;
    
    try {
      await deleteClient(client.id);
      toast.success(t('clients.messages.deleted'));
      loadClients();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const filteredClients = clients.filter(client => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchLower) ||
      client.contact_email?.toLowerCase().includes(searchLower) ||
      client.vat_number?.toLowerCase().includes(searchLower)
    );
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('clients.title')}</h1>
          <p className="text-gray-500">
            {filteredClients.length} {uiLang === 'lt' ? 'klientų' : 'clients'}
          </p>
        </div>
        <button
          onClick={() => { setEditingClient(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
          data-testid="new-client-button"
        >
          <Plus size={20} />
          {t('clients.newClient')}
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={t('clients.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Clients Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('clients.noClients')}</h3>
          <p className="text-gray-500 mb-4">
            {search 
              ? (uiLang === 'lt' ? 'Pagal jūsų paiešką nieko nerasta' : 'No results found for your search')
              : t('clients.createFirst')}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{client.name}</h3>
                  {client.vat_number && (
                    <p className="text-sm text-gray-500">
                      {uiLang === 'lt' ? 'PVM' : 'VAT'}: {client.vat_number}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditingClient(client); setShowModal(true); }}
                    className="p-2 text-gray-400 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(client)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {client.address && (
                  <div className="flex items-start gap-2 text-gray-600">
                    <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{client.address}</span>
                  </div>
                )}
                {client.contact_name && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 size={14} />
                    <span>{client.contact_name}</span>
                  </div>
                )}
                {client.contact_email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={14} />
                    <a href={`mailto:${client.contact_email}`} className="hover:text-[#0F7D70]">
                      {client.contact_email}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                <span className={`
                  px-2 py-0.5 rounded text-xs font-medium
                  ${client.country === 'LT' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}
                `}>
                  {client.country || 'LT'}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {client.language?.toUpperCase() || 'LT'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <ClientModal
          client={editingClient}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            loadClients();
          }}
        />
      )}
    </div>
  );
};

// Client Modal
const ClientModal = ({ client, onClose, onSaved }) => {
  const { t, language: uiLang } = useLanguage();
  const [form, setForm] = useState({
    name: client?.name || '',
    country: client?.country || 'LT',
    language: client?.language || 'lt',
    vat_number: client?.vat_number || '',
    address: client?.address || '',
    contact_name: client?.contact_name || '',
    contact_email: client?.contact_email || ''
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error(t('clients.messages.nameRequired'));
      return;
    }
    
    setSaving(true);
    try {
      if (client) {
        await updateClient(client.id, form);
        toast.success(t('clients.messages.updated'));
      } else {
        await createClient(form);
        toast.success(t('clients.messages.created'));
      }
      onSaved();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 size={20} className="text-[#0F7D70]" />
            {client 
              ? (uiLang === 'lt' ? 'Redaguoti klientą' : 'Edit Client')
              : t('clients.newClient')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.form.name')} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.form.country')}
              </label>
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              >
                <option value="LT">{uiLang === 'lt' ? 'Lietuva' : 'Lithuania'}</option>
                <option value="LV">{uiLang === 'lt' ? 'Latvija' : 'Latvia'}</option>
                <option value="EE">{uiLang === 'lt' ? 'Estija' : 'Estonia'}</option>
                <option value="PL">{uiLang === 'lt' ? 'Lenkija' : 'Poland'}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.form.language')}
              </label>
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              >
                <option value="lt">Lietuvių</option>
                <option value="lv">Latviešu</option>
                <option value="et">Eesti</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.form.vatNumber')}
            </label>
            <input
              type="text"
              value={form.vat_number}
              onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
              placeholder="LT123456789"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('clients.form.address')}
            </label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.form.contactName')}
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.form.contactEmail')}
              </label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? t('common.loading') : (
                <>
                  <Save size={18} />
                  {client ? t('common.save') : t('common.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QdClients;
